import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditProductModal from 'components/admin/common/modal/product/EditProductModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';
import usePost from 'core/hooks/api/usePost';
import usePut from 'core/hooks/api/usePut';
import useDelete from 'core/hooks/api/useDelete';
import moment from 'moment';
import ImageWithBaseApi from 'components/common/mediaRender/ImageWithBaseApi';

const Products = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createProductData,
        postError: creatingError,
        postLoading: creating,
        putData: updateProductData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteProductData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.PRODUCTS);

    const { fetchData: productHelpData } = useCrud(API_ENDPOINTS.PRODUCT_HELP);
    const { fetchData: categoryData } = useCrud(API_ENDPOINTS.CATEGORIES);
    const { putData: updateProductDetail, loading: updatingProductDetail } = usePut(API_ENDPOINTS.PRODUCT_DETAILS);
    const { postData: createProductDetail, loading: creatingProductDetail } = usePost(API_ENDPOINTS.PRODUCT_DETAILS);
    const { deleteData: deleteProductDetail, loading: deletingProductDetail } = useDelete(API_ENDPOINTS.PRODUCT_DETAILS);
    const { postData: createProductPhoto, loading: uploadingImages } = usePost(API_ENDPOINTS.PRODUCT_PHOTOS);
    const { deleteData: deleteProductPhoto } = useDelete(API_ENDPOINTS.PRODUCT_PHOTOS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteProduct = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditProduct = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteProduct, modalData: productToDelete, openModal: openDeleteProduct, closeModal: closedDeleteProduct } = useModalState({
        onClose: handleCloseDeleteProduct
    });

    const { isOpen: isOpenEditProduct, modalData: selectedProduct, openModal: openEditProduct, closeModal: closedEditProduct } = useModalState({
        onClose: handleCloseEditProduct
    });

    const validationRules = [
        { field: 'title', method: 'required' },
        { field: 'category_id', method: 'required' },
        { field: 'description', method: 'required' },
    ];

    const validationFeaturesRules = [
        { field: 'attribute', method: 'required' },
        { field: 'label', method: 'required' },
    ];

    const categoriesOptions = useMemo(() => {
        if (categoryData?.length > 0) {
            return categoryData.map(client => ({
                label: client.title,
                value: client.id
            }));
        }
    }, [categoryData]);

    const productHelpOptions = useMemo(() => {
        if (productHelpData?.length > 0) {
            return productHelpData.map(help => ({
                label: help.title,
                value: help.id
            }));
        }
    }, [productHelpData]);

    const handleDeleteProductPhoto = async (id) => {
        try {
            await deleteProductPhoto(id);
            refetch();
        } catch (error) {
            console.error(error);
        }
    };

    const handleModalSave = useCallback(async (updatedProduct, galleryImages, features, featuresToDelete) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedProduct, validationRules);
        const featuresErrors = features.map((item, index) => {
            const featureErrors = validateForm(item, validationFeaturesRules);
            return Object.keys(featureErrors).reduce((acc, key) => {
                if (!acc[index]) acc[index] = {};
                acc[index][key] = featureErrors[key];
                return acc;
            }, {});
        }).filter(error => Object.keys(error).length > 0);

        const combinedErrors = { ...formErrors, ...featuresErrors.reduce((acc, curr) => ({ ...acc, ...curr }), {}) };

        if (Object.keys(combinedErrors).length > 0) {
            setErrors(combinedErrors);
            return;
        }

        setErrors({});
        const forms = galleryImages
            ?.filter((image) => {
                const fileType = image.file?.type || image.file?.path.split('.').pop();
                return image.file && image.file?.path && !image.file.path.startsWith('storage/') && (fileType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif'].includes(fileType));
            })
            ?.map((image) => ({
                product_id: updatedProduct.id || null,
                type: 'image',
                src: image.file,
            }));

        try {
            const formattedProduct = {
                ...updatedProduct,
                date: moment(updatedProduct.date).format('YYYY-MM-DD')
            };

            if (updatedProduct.id) {
                const { filter_options, photos, product_option, details, specifications, applied_photos, ...productData } = formattedProduct;
                await updateProductData(updatedProduct.id, productData);
                if (forms.length > 0) {
                    for (const form of forms) {
                        form.product_id = updatedProduct.id;
                        await createProductPhoto(form, true);
                    }
                }
                for (const feature of features) {
                    if (feature.label.trim() === '' && feature.attribute.trim() === '') {
                        continue;
                    }
                    feature.product_id = updatedProduct.id;
                    if (feature.id) {
                        await updateProductDetail(feature.id, feature);
                    } else {
                        await createProductDetail(feature);
                    }
                }
                for (const featureId of featuresToDelete) {
                    await deleteProductDetail(featureId);
                }
                setSuccessMessage("Producto actualizado con éxito.");
            } else {
                const createdProduct = await createProductData(formattedProduct);
                if (forms.length > 0) {
                    for (const form of forms) {
                        form.product_id = createdProduct.id;
                        await createProductPhoto(form, true);
                    }
                }
                for (const feature of features) {
                    if (feature.label.trim() === '' && feature.attribute.trim() === '') {
                        continue;
                    }
                    feature.product_id = createdProduct.id;
                    await createProductDetail(feature);
                }
                setSuccessMessage("Producto creado con éxito.");
            }
            closedEditProduct();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateProductData, createProductData, createProductPhoto, createProductDetail, updateProductDetail, deleteProductDetail, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newProduct = {
            title: '',
            category: '',
            tags: '',
            description: '',
        };
        openEditProduct(newProduct);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (productToDelete) {
            try {
                await deleteProductData(productToDelete.id);
                setSuccessMessage("Producto eliminado con éxito.");
                refetch();
                closedDeleteProduct();
            } catch (error) {
                console.error(error);
            }
        }
    }, [productToDelete, deleteProductData, refetch]);

    const columns = useMemo(() => [
        {
            field: 'photos',
            header: 'Foto',
            body: (rowData) => rowData.photos?.length > 0 ? (
                <ImageWithBaseApi
                    src={rowData.photos[0].src}
                    alt={rowData.title}
                    width="50"
                    height="50"
                    className="product-photo"
                />
            ) : null
        },
        { field: 'title', header: 'Título', sortable: true },
        { field: 'description', header: 'Descripción', sortable: true },
        { field: 'category_name', header: 'Categoria', sortable: true },
        {
            field: 'tags',
            header: 'Tags de Busqueda',
            sortable: true,
            body: (rowData) => rowData.tags?.length > 0 ? rowData.tags.join(' - ') : ""
        }
    ], []);

    const memoizedGalleryImages = useMemo(() => selectedProduct?.photos || [], [selectedProduct]);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Productos</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Producto
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditProduct}
                onDelete={openDeleteProduct}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="title"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditProductModal
                show={isOpenEditProduct}
                product={selectedProduct}
                handleClose={closedEditProduct}
                handleSave={handleModalSave}
                updating={updating || creating || uploadingImages || updatingProductDetail || creatingProductDetail || deletingProductDetail}
                errors={errors}
                requestError={creatingError || updatingError}
                deleteMediaItem={handleDeleteProductPhoto}
                initialImages={memoizedGalleryImages}
                categoriesOptions={categoriesOptions}
                productHelpOptions={productHelpOptions}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteProduct}
                onHide={closedDeleteProduct}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Products);